import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { useToast } from './Toast';
import {
  ShoppingBag, Plus, Edit2, Trash2, Package, X, Save, Upload,
  ChevronDown, Tag, AlertCircle, Check, Truck, Clock, Ban,
  ExternalLink, Copy, RefreshCw, Image as ImageIcon, Palette,
  Trash as TrashIcon,
} from 'lucide-react';

const STATUS_CONFIG = {
  'En attente': { badge: 'bg-amber-100 text-amber-700',  dot: 'bg-amber-400',   icon: Clock  },
  'Confirmé':   { badge: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500',    icon: Check  },
  'Livré':      { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', icon: Truck },
  'Annulé':     { badge: 'bg-red-100 text-red-600',      dot: 'bg-red-400',     icon: Ban    },
};

const STATUSES = ['En attente', 'Confirmé', 'Livré', 'Annulé'];

export default function AdminShop({ salon, onShopSlugGenerated }) {
  const showToast = useToast();
  const [subTab, setSubTab]         = useState('produits');
  const [products, setProducts]     = useState([]);
  const [orders, setOrders]         = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [orderFilter, setOrderFilter] = useState('');
  const [editingProduct, setEditingProduct] = useState(null); // null | 'new' | product
  const [generatingSlug, setGeneratingSlug] = useState(false);
  const [copied, setCopied]         = useState(false);

  const shopUrl = salon?.shopSlug
    ? `${window.location.origin}/shop/${salon.shopSlug}`
    : null;

  useEffect(() => {
    fetchProducts();
    fetchOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/admin/products');
      setProducts(res.data.data);
      const cats = [...new Set(res.data.data.map(p => p.categorie).filter(Boolean))];
      setCategories(cats);
    } catch (e) {
      showToast('Erreur chargement produits', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await api.get(`/admin/orders${orderFilter ? `?status=${orderFilter}` : ''}`);
      setOrders(res.data.data);
    } catch (e) { /* silently ignore */ }
  };

  useEffect(() => { fetchOrders(); }, [orderFilter]); // eslint-disable-line

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Supprimer "${product.nom}" ?`)) return;
    try {
      await api.delete(`/admin/products/${product._id}`);
      showToast('Produit supprimé');
      fetchProducts();
    } catch (e) {
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status, methodePaiement) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status, methodePaiement });
      showToast(`Commande marquée "${status}"`);
      fetchOrders();
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur', 'error');
    }
  };

  const handleGenerateSlug = async () => {
    setGeneratingSlug(true);
    try {
      await api.post('/salon/me/generate-shop-slug');
      onShopSlugGenerated();
      showToast('Lien boutique généré !');
    } catch (e) {
      showToast('Erreur génération du lien', 'error');
    } finally {
      setGeneratingSlug(false);
    }
  };

  const handleCopy = () => {
    if (!shopUrl) return;
    navigator.clipboard.writeText(shopUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">

      {/* ── En-tête ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-slate-800">Boutique en ligne</h2>
          <p className="text-sm text-slate-500 font-medium mt-0.5">Gérez vos produits et vos commandes</p>
        </div>
        {subTab === 'produits' && (
          <button
            onClick={() => setEditingProduct('new')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all"
          >
            <Plus size={18} /> Ajouter un produit
          </button>
        )}
      </div>

      {/* ── Lien boutique ── */}
      {shopUrl ? (
        <div className="p-4 rounded-2xl border bg-blue-50 border-blue-100 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <ShoppingBag size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-0.5">Lien de votre boutique</p>
              <p className="text-sm font-bold text-blue-700 truncate">{shopUrl}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleCopy}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black transition-all ${copied ? 'bg-emerald-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300'}`}>
              {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier</>}
            </button>
            <a href={shopUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black bg-white border border-slate-200 text-slate-600 hover:border-blue-300 transition-all">
              <ExternalLink size={13} /> Voir
            </a>
          </div>
        </div>
      ) : (
        <div className="p-5 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-slate-200 text-slate-400 flex items-center justify-center shrink-0">
              <ShoppingBag size={18} />
            </div>
            <div>
              <p className="font-bold text-slate-700 text-sm">Votre boutique n'est pas encore en ligne</p>
              <p className="text-xs text-slate-400 mt-0.5">Générez votre lien unique pour partager votre boutique</p>
            </div>
          </div>
          <button
            onClick={handleGenerateSlug}
            disabled={generatingSlug}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-black bg-blue-600 text-white hover:bg-blue-700 transition-all disabled:opacity-60 shrink-0 w-full sm:w-auto"
          >
            <RefreshCw size={14} className={generatingSlug ? 'animate-spin' : ''} />
            {generatingSlug ? 'Génération…' : 'Activer ma boutique'}
          </button>
        </div>
      )}

      {/* ── Sous-tabs ── */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-2xl w-full sm:w-fit overflow-x-auto no-scrollbar">
        {[
          { id: 'produits',   label: 'Produits',   icon: Package    },
          { id: 'commandes',  label: 'Commandes',  icon: ShoppingBag },
          { id: 'apparence',  label: 'Apparence',  icon: Palette    },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
              subTab === tab.id
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.id === 'commandes' && orders.filter(o => o.status === 'En attente').length > 0 && (
              <span className="w-5 h-5 bg-amber-400 text-white rounded-full text-[10px] font-black flex items-center justify-center">
                {orders.filter(o => o.status === 'En attente').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Vue Produits ── */}
      {subTab === 'produits' && (
        <ProductsTab
          products={products}
          categories={categories}
          loading={loading}
          onEdit={setEditingProduct}
          onDelete={handleDeleteProduct}
        />
      )}

      {/* ── Vue Commandes ── */}
      {subTab === 'commandes' && (
        <OrdersTab
          orders={orders}
          filter={orderFilter}
          onFilterChange={setOrderFilter}
          onStatusChange={handleUpdateOrderStatus}
          devise={salon?.devise}
        />
      )}

      {/* ── Vue Apparence ── */}
      {subTab === 'apparence' && (
        <AppearanceTab salon={salon} onSaved={onShopSlugGenerated} />
      )}

      {/* ── Modal produit ── */}
      {editingProduct !== null && (
        <ProductModal
          product={editingProduct === 'new' ? null : editingProduct}
          categories={categories}
          onClose={() => setEditingProduct(null)}
          onSaved={() => { setEditingProduct(null); fetchProducts(); }}
        />
      )}
    </div>
  );
}

// ─── Onglet Produits ─────────────────────────────────────────────────────────

function ProductsTab({ products, categories, loading, onEdit, onDelete }) {
  const [filterCat, setFilterCat] = useState('');
  const visible = filterCat ? products.filter(p => p.categorie === filterCat) : products;

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Filtre catégorie */}
      {categories.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
          <button
            onClick={() => setFilterCat('')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0 ${!filterCat ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
          >
            Tous
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCat(cat === filterCat ? '' : cat)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all shrink-0 ${filterCat === cat ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-white rounded-[2rem] border border-slate-100 p-16 text-center">
          <Package size={40} className="mx-auto text-slate-200 mb-4" />
          <p className="font-bold text-slate-400">Aucun produit pour l'instant</p>
          <p className="text-sm text-slate-300 mt-1">Ajoutez votre premier produit avec le bouton ci-dessus</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {visible.map(product => (
            <ProductCard key={product._id} product={product} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onEdit, onDelete }) {
  const stockColor = product.stock === 0
    ? 'bg-red-100 text-red-600'
    : product.stock <= 5
    ? 'bg-amber-100 text-amber-600'
    : 'bg-emerald-100 text-emerald-600';

  return (
    <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="aspect-square bg-slate-50 relative overflow-hidden">
        {product.image ? (
          <img src={product.image} alt={product.nom}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={40} className="text-slate-200" />
          </div>
        )}
        {/* Badge stock */}
        <div className={`absolute top-2 right-2 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-black ${stockColor}`}>
          {product.stock === 0 ? 'Rupture' : `${product.stock} en stock`}
        </div>
        {/* Badge inactif */}
        {!product.isActive && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center">
            <span className="px-3 py-1 bg-slate-800 text-white text-xs font-black rounded-xl">Masqué</span>
          </div>
        )}
        {/* Actions */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          <button onClick={() => onEdit(product)}
            className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl flex items-center justify-center shadow-md hover:bg-blue-50 text-blue-600 transition-colors">
            <Edit2 size={13} />
          </button>
          <button onClick={() => onDelete(product)}
            className="w-7 h-7 sm:w-8 sm:h-8 bg-white rounded-xl flex items-center justify-center shadow-md hover:bg-red-50 text-red-500 transition-colors">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      {/* Infos */}
      <div className="p-2.5 sm:p-4">
        {product.categorie && (
          <span className="text-[9px] sm:text-[10px] font-black text-blue-500 uppercase tracking-widest">{product.categorie}</span>
        )}
        <p className="font-black text-slate-800 text-xs sm:text-sm mt-0.5 line-clamp-2 leading-snug">{product.nom}</p>
        <p className="text-sm sm:text-base font-black text-slate-900 mt-1.5">{product.prix?.toLocaleString()}
          <span className="text-[10px] sm:text-xs font-bold text-slate-400 ml-0.5">FCFA</span>
        </p>
      </div>
    </div>
  );
}

// ─── Onglet Commandes ─────────────────────────────────────────────────────────

function OrdersTab({ orders, filter, onFilterChange, onStatusChange, devise }) {
  return (
    <div className="space-y-4">
      {/* Filtre statut */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        <button
          onClick={() => onFilterChange('')}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 transition-all ${!filter ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
        >
          Toutes
        </button>
        {STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          return (
            <button key={s} onClick={() => onFilterChange(filter === s ? '' : s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap shrink-0 transition-all ${filter === s ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {s}
            </button>
          );
        })}
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-[2rem] border border-slate-100 p-16 text-center">
          <ShoppingBag size={40} className="mx-auto text-slate-200 mb-4" />
          <p className="font-bold text-slate-400">Aucune commande</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => (
            <OrderCard key={order._id} order={order} onStatusChange={onStatusChange} devise={devise} />
          ))}
        </div>
      )}
    </div>
  );
}

const getPaymentMethods = (devise) => {
  if (devise === 'EUR') return ['Especes', 'Carte'];
  return ['Especes', 'Wave', 'Orange Money'];
};

const PAYMENT_LABELS = {
  'Especes':      'Espèces',
  'Wave':         'Wave',
  'Orange Money': 'Orange Money',
  'Carte':        'Carte Bancaire',
};

function OrderCard({ order, onStatusChange, devise }) {
  const methods = getPaymentMethods(devise);
  const [expanded, setExpanded] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [methodePaiement, setMethodePaiement] = useState(methods[0]);
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG['En attente'];
  const StatusIcon = cfg.icon;
  const date = new Date(order.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric'
  });

  const nextStatuses = STATUSES.filter(s => s !== order.status);

  const handleStatusClick = (s) => {
    if (s === 'Livré') { setPaymentModal(true); return; }
    onStatusChange(order._id, s);
  };

  const confirmLivraison = () => {
    onStatusChange(order._id, 'Livré', methodePaiement);
    setPaymentModal(false);
  };

  return (
    <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${cfg.badge}`}>
            <StatusIcon size={18} />
          </div>
          <div className="min-w-0">
            <p className="font-black text-slate-800 text-sm truncate">
              {order.client.prenom} {order.client.nom}
            </p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              {order.items.length} article{order.items.length > 1 ? 's' : ''} · {date}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="font-black text-slate-800">{order.total?.toLocaleString()}</p>
            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black ${cfg.badge}`}>{order.status}</span>
          </div>
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-50">
          {/* Infos client */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoRow label="Téléphone" value={order.client.telephone} />
            <InfoRow label="Email"     value={order.client.email} />
            <InfoRow label="Adresse"   value={order.client.adresse} className="sm:col-span-2" />
            {order.note && <InfoRow label="Note" value={order.note} className="sm:col-span-2" />}
          </div>

          {/* Articles */}
          <div className="space-y-2">
            {order.items.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl">
                {item.image ? (
                  <img src={item.image} alt={item.nom} className="w-10 h-10 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 bg-slate-200 rounded-xl flex items-center justify-center shrink-0">
                    <Package size={16} className="text-slate-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-700 text-sm truncate">{item.nom}</p>
                  <p className="text-xs text-slate-400">×{item.quantite} · {item.prix?.toLocaleString()}</p>
                </div>
                <p className="font-black text-slate-800 text-sm shrink-0">
                  {(item.prix * item.quantite).toLocaleString()}
                </p>
              </div>
            ))}
            <div className="flex justify-end pt-1">
              <span className="font-black text-slate-800">Total : {order.total?.toLocaleString()}</span>
            </div>
          </div>

          {/* Actions statut */}
          {order.status !== 'Livré' && order.status !== 'Annulé' && (
            <div className="flex flex-wrap gap-2 pt-1">
              {nextStatuses.map(s => {
                const c = STATUS_CONFIG[s];
                return (
                  <button key={s} onClick={() => handleStatusClick(s)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${c.badge} hover:opacity-80`}>
                    <c.icon size={13} /> Marquer "{s}"
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal paiement pour "Livré" */}
      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setPaymentModal(false)}>
          <div className="bg-white rounded-[2rem] shadow-2xl p-6 w-full max-w-sm space-y-5" onClick={e => e.stopPropagation()}>
            <div>
              <p className="font-black text-slate-800 text-lg">Moyen de paiement</p>
              <p className="text-sm text-slate-400 mt-0.5">Comment le client a-t-il payé cette commande ?</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {methods.map(m => (
                <button key={m} type="button"
                  onClick={() => setMethodePaiement(m)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                    methodePaiement === m
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300'
                  }`}>
                  {PAYMENT_LABELS[m] || m}
                </button>
              ))}
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setPaymentModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
                Annuler
              </button>
              <button onClick={confirmLivraison}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-all flex items-center justify-center gap-1.5">
                <Truck size={15} /> Confirmer livraison
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, className = '' }) {
  return (
    <div className={`bg-slate-50 rounded-xl px-3 py-2.5 ${className}`}>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className="text-sm font-bold text-slate-700 mt-0.5">{value}</p>
    </div>
  );
}

// ─── Modal Produit (Ajout / Édition) ─────────────────────────────────────────

function ProductModal({ product, categories, onClose, onSaved }) {
  const showToast = useToast();
  const [saving, setSaving]   = useState(false);
  const [preview, setPreview] = useState(product?.image || null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    nom:         product?.nom         || '',
    description: product?.description || '',
    prix:        product?.prix        || '',
    stock:       product?.stock       ?? '',
    categorie:   product?.categorie   || '',
    isActive:    product?.isActive    ?? true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [newCategorie, setNewCategorie] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);

  const set = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { showToast('Le nom est requis', 'error'); return; }
    if (form.prix === '' || isNaN(Number(form.prix))) { showToast('Prix invalide', 'error'); return; }

    setSaving(true);
    try {
      const categorie = showNewCat ? newCategorie.trim() : form.categorie;
      const fd = new FormData();
      fd.append('nom',         form.nom.trim());
      fd.append('description', form.description.trim());
      fd.append('prix',        form.prix);
      fd.append('stock',       form.stock || 0);
      fd.append('categorie',   categorie);
      fd.append('isActive',    form.isActive);
      if (imageFile) fd.append('image', imageFile);

      if (product) {
        await api.put(`/admin/products/${product._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Produit mis à jour !');
      } else {
        await api.post('/admin/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Produit ajouté !');
      }
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-lg shadow-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="p-6 pb-4 border-b border-slate-100 shrink-0 flex items-center justify-between">
          <h2 className="text-xl font-black text-slate-900">
            {product ? 'Modifier le produit' : 'Nouveau produit'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Upload image */}
          <div
            className="relative border-2 border-dashed border-slate-200 rounded-2xl overflow-hidden cursor-pointer hover:border-blue-400 transition-colors group"
            style={{ aspectRatio: '16/9' }}
            onClick={() => fileRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
          >
            {preview ? (
              <>
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex items-center gap-2 text-white font-bold text-sm">
                    <Upload size={18} /> Changer l'image
                  </div>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300">
                <ImageIcon size={36} />
                <p className="font-bold text-sm">Cliquer ou glisser une image</p>
                <p className="text-xs font-medium">JPG, PNG, WEBP · 5 MB max</p>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Nom */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nom du produit *</label>
            <input
              type="text" required
              value={form.nom} onChange={set('nom')}
              placeholder="Ex : Sérum vitamine C"
              className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 focus:ring-2 ring-blue-500/20"
            />
          </div>

          {/* Prix + Stock */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prix *</label>
              <input
                type="number" min="0" required
                value={form.prix} onChange={set('prix')}
                placeholder="5000"
                className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 focus:ring-2 ring-blue-500/20"
              />
            </div>
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Stock</label>
              <input
                type="number" min="0"
                value={form.stock} onChange={set('stock')}
                placeholder="10"
                className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 focus:ring-2 ring-blue-500/20"
              />
            </div>
          </div>

          {/* Catégorie */}
          <div>
            <div className="flex items-center justify-between ml-1 mb-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Catégorie</label>
              <button type="button" onClick={() => setShowNewCat(!showNewCat)}
                className="text-[10px] font-black text-blue-500 hover:text-blue-600 flex items-center gap-1">
                <Tag size={10} /> {showNewCat ? 'Choisir existante' : 'Nouvelle catégorie'}
              </button>
            </div>
            {showNewCat ? (
              <input
                type="text"
                value={newCategorie} onChange={e => setNewCategorie(e.target.value)}
                placeholder="Ex : Soins capillaires"
                className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-800 focus:ring-2 ring-blue-500/20"
              />
            ) : (
              <div className="relative">
                <select value={form.categorie} onChange={set('categorie')}
                  className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-bold text-slate-700 appearance-none cursor-pointer focus:ring-2 ring-blue-500/20">
                  <option value="">- Sans catégorie -</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
            <textarea
              value={form.description} onChange={set('description')}
              placeholder="Décrivez votre produit..."
              className="w-full mt-1 p-4 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 h-24 resize-none focus:ring-2 ring-blue-500/20"
            />
          </div>

          {/* Visibilité */}
          <label className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl cursor-pointer hover:bg-slate-100 transition-colors">
            <div className={`w-10 h-6 rounded-full transition-colors relative ${form.isActive ? 'bg-blue-500' : 'bg-slate-300'}`}>
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-sm ${form.isActive ? 'left-5' : 'left-1'}`} />
            </div>
            <div>
              <p className="font-bold text-slate-700 text-sm">Produit visible sur la boutique</p>
              <p className="text-xs text-slate-400 font-medium">{form.isActive ? 'Les clients peuvent voir et commander ce produit' : 'Produit masqué du public'}</p>
            </div>
            <input type="checkbox" className="hidden" checked={form.isActive} onChange={set('isActive')} />
          </label>
        </form>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-slate-100 shrink-0">
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 py-3.5 rounded-2xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all">
              Annuler
            </button>
            <button onClick={handleSubmit} disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-60">
              <Save size={16} />
              {saving ? 'Enregistrement…' : product ? 'Mettre à jour' : 'Ajouter le produit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Apparence ─────────────────────────────────────────────────────────

function AppearanceTab({ salon, onSaved }) {
  const showToast = useToast();
  const heroInputRef = useRef(null);

  const [accentColor,   setAccentColor]   = useState(salon?.shopAccentColor  || '#B08D57');
  const [darkColor,     setDarkColor]     = useState(salon?.shopDarkColor    || '#0E0D0B');
  const [tagline,       setTagline]       = useState(salon?.shopTagline       || '');
  const [paymentLink,   setPaymentLink]   = useState(salon?.shopPaymentLink   || '');
  const [heroPreview,   setHeroPreview]   = useState(salon?.shopHeroImage     || '');

  const [savingTheme, setSavingTheme]   = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [deletingHero,  setDeletingHero]  = useState(false);

  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      await api.put('/salon/me/shop-theme', { shopAccentColor: accentColor, shopDarkColor: darkColor, shopTagline: tagline, shopPaymentLink: paymentLink });
      showToast('Thème enregistré !');
      onSaved();
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSavingTheme(false);
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setUploadingHero(true);
    try {
      const res = await api.post('/salon/me/shop-hero', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroPreview(res.data.data.shopHeroImage);
      showToast('Photo héro mise à jour !');
      onSaved();
    } catch (e) {
      showToast(e.response?.data?.message || 'Erreur upload', 'error');
    } finally {
      setUploadingHero(false);
      e.target.value = '';
    }
  };

  const handleDeleteHero = async () => {
    if (!window.confirm('Supprimer la photo héro ? Le fond noir par défaut sera restauré.')) return;
    setDeletingHero(true);
    try {
      await api.delete('/salon/me/shop-hero');
      setHeroPreview('');
      showToast('Photo supprimée');
      onSaved();
    } catch (e) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setDeletingHero(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">

      {/* ── Photo héro ── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden">
        <div className="p-6 pb-4">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Photo d'en-tête</p>
          <h3 className="font-black text-slate-800 text-base">Arrière-plan de la boutique</h3>
          <p className="text-xs text-slate-400 font-medium mt-1">Remplace le fond noir par défaut. Recommandé : 1920 × 1080 px, moins de 10 Mo.</p>
        </div>

        {/* Aperçu */}
        <div className="relative mx-6 mb-4 rounded-2xl overflow-hidden bg-slate-900" style={{ height: '180px' }}>
          {heroPreview ? (
            <img src={heroPreview} alt="Héro" className="w-full h-full object-cover opacity-80" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <ImageIcon size={28} className="text-slate-600" />
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fond par défaut</p>
            </div>
          )}
          {/* Overlay preview text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/60 text-lg font-serif italic">{salon?.nom}</p>
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={() => heroInputRef.current?.click()}
            disabled={uploadingHero}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-slate-800 text-white text-sm font-bold hover:bg-slate-700 transition-all disabled:opacity-60"
          >
            {uploadingHero ? (
              <><RefreshCw size={14} className="animate-spin" /> Upload…</>
            ) : (
              <><Upload size={14} /> {heroPreview ? 'Changer la photo' : 'Ajouter une photo'}</>
            )}
          </button>
          {heroPreview && (
            <button
              onClick={handleDeleteHero}
              disabled={deletingHero}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-red-100 text-red-500 text-sm font-bold hover:bg-red-50 transition-all disabled:opacity-60"
            >
              {deletingHero ? <RefreshCw size={14} className="animate-spin" /> : <TrashIcon size={14} />}
            </button>
          )}
        </div>
        <input ref={heroInputRef} type="file" accept="image/*" className="hidden" onChange={handleHeroUpload} />
      </div>

      {/* ── Couleurs ── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 p-6 space-y-5">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Couleurs</p>
          <h3 className="font-black text-slate-800 text-base">Palette de la boutique</h3>
          <p className="text-xs text-slate-400 font-medium mt-1">Ces couleurs s'appliquent aux boutons, prix, accents et au fond principal.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Couleur accent */}
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
            <label className="relative cursor-pointer shrink-0" title="Couleur d'accent">
              <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                className="sr-only" />
              <div className="w-12 h-12 rounded-xl shadow-sm border-2 border-white ring-1 ring-slate-200 transition-transform hover:scale-105"
                style={{ background: accentColor }} />
            </label>
            <div className="min-w-0">
              <p className="text-xs font-black text-slate-600 uppercase tracking-wide">Couleur accent</p>
              <p className="text-[10px] text-slate-400 font-medium mt-0.5">Boutons, prix, dorures</p>
              <p className="text-xs font-mono text-slate-500 mt-1">{accentColor}</p>
            </div>
          </div>

          {/* Couleur sombre */}
          <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
            <label className="relative cursor-pointer shrink-0" title="Couleur sombre">
              <input type="color" value={darkColor} onChange={e => setDarkColor(e.target.value)}
                className="sr-only" />
              <div className="w-12 h-12 rounded-xl shadow-sm border-2 border-white ring-1 ring-slate-200 transition-transform hover:scale-105"
                style={{ background: darkColor }} />
            </label>
            <div className="min-w-0">
              <p className="text-xs font-black text-slate-600 uppercase tracking-wide">Couleur sombre</p>
              <p className="text-[10px] text-slate-400 font-medium mt-0.5">Textes, fonds, panier</p>
              <p className="text-xs font-mono text-slate-500 mt-1">{darkColor}</p>
            </div>
          </div>
        </div>

        {/* Aperçu palette */}
        <div className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100">
          <div className="w-8 h-8 rounded-xl" style={{ background: darkColor }} />
          <div className="w-8 h-8 rounded-xl" style={{ background: accentColor }} />
          <div className="w-8 h-8 rounded-xl bg-white border border-slate-100" />
          <div className="flex-1 h-px" style={{ background: accentColor, opacity: 0.3 }} />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Aperçu palette</span>
        </div>

        {/* Boutons reset */}
        <button
          onClick={() => { setAccentColor('#B08D57'); setDarkColor('#0E0D0B'); }}
          className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
        >
          Restaurer les couleurs par défaut
        </button>
      </div>

      {/* ── Tagline ── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 p-6 space-y-4">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Texte héro</p>
          <h3 className="font-black text-slate-800 text-base">Accroche de la boutique</h3>
          <p className="text-xs text-slate-400 font-medium mt-1">Phrase affichée sous le titre en haut de la boutique. Par défaut : "Beauté · Soins · Prestige".</p>
        </div>
        <input
          type="text"
          value={tagline}
          onChange={e => setTagline(e.target.value)}
          placeholder="Beauté · Soins · Prestige"
          maxLength={80}
          className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
        />
        {tagline && (
          <p className="text-[10px] text-slate-400 font-medium">Aperçu : <span className="text-slate-600 italic">{tagline}</span></p>
        )}
      </div>

      {/* ── Lien de paiement ── */}
      <div className="bg-white rounded-[2rem] border border-slate-100 p-6 space-y-4">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Paiement</p>
          <h3 className="font-black text-slate-800 text-base">Lien de paiement</h3>
          <p className="text-xs text-slate-400 font-medium mt-1">Lien vers votre solution de paiement (Wave, Orange Money, Stripe…). Utilisez <span className="font-mono text-slate-600">{'{montant}'}</span> dans l'URL pour pré-remplir le montant de la commande.</p>
        </div>
        <div className="relative">
          <input
            type="url"
            value={paymentLink}
            onChange={e => setPaymentLink(e.target.value)}
            placeholder="https://pay.wave.com/…"
            className="w-full p-4 bg-slate-50 rounded-2xl outline-none font-medium text-slate-700 text-sm focus:ring-2 ring-blue-500/20 pr-20"
          />
          {paymentLink && (
            <a href={paymentLink} target="_blank" rel="noreferrer"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-blue-500 hover:text-blue-600 flex items-center gap-1">
              <ExternalLink size={11} /> Tester
            </a>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-100">
          <AlertCircle size={13} className="text-amber-500 shrink-0" />
          <p className="text-[11px] font-medium text-amber-700">Le bouton de paiement s'affiche automatiquement après confirmation de commande.</p>
        </div>
      </div>

      {/* ── Sauvegarder ── */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveTheme}
          disabled={savingTheme}
          className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-slate-800 text-white font-bold text-sm shadow-lg shadow-slate-200 hover:bg-slate-700 transition-all disabled:opacity-60"
        >
          <Save size={15} />
          {savingTheme ? 'Enregistrement…' : 'Enregistrer le thème'}
        </button>
      </div>
    </div>
  );
}
